<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { Virtualizer } from 'virtua/vue';
import { useBreakpoints } from '@vueuse/core';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import { useConversationScrollAnchor } from 'dashboard/composables/chatlist/useConversationScrollAnchor';
import ConversationItem from './ConversationItem.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';

import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  conversationList: { type: Array, default: () => [] },
  pinnedConversationIds: { type: Array, default: () => [] },
  hideLoadingIndicator: { type: Boolean, default: false },
  isRefreshing: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  showEndOfListMessage: { type: Boolean, default: false },
  label: { type: String, default: '' },
  teamId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  conversationType: { type: String, default: '' },
  showAssignee: { type: Boolean, default: false },
  isOnExpandedLayout: { type: Boolean, default: false },
});

const emit = defineEmits(['loadMore']);

const lastPinnedId = computed(() => {
  const pinned = new Set(props.pinnedConversationIds.map(Number));
  return props.conversationList
    .filter(item => pinned.has(Number(item.id)))
    .at(-1)?.id;
});

const conversationListRef = ref(null);
const virtualListRef = ref(null);
const movedConversationDirections = ref(new Map());
const newConversationIds = ref(new Set());
const movementAnimationTimer = ref(null);
const isUserScrolling = ref(false);
const scrollIdleTimer = ref(null);
const isLoadMoreArmed = ref(false);

const breakpoints = useBreakpoints({
  lg: wootConstants.LARGE_SCREEN_BREAKPOINT,
});
const isLgScreen = breakpoints.greaterOrEqual('lg');
const showExpandedCards = computed(
  () => props.isOnExpandedLayout && isLgScreen.value
);

useChatListKeyboardEvents(conversationListRef);

const conversationIds = computed(() =>
  props.conversationList.map(conversation => conversation.id)
);
useConversationScrollAnchor(conversationIds, virtualListRef);

const intersectionObserverOptions = computed(() => ({
  root: conversationListRef.value,
  rootMargin: '100px 0px 100px 0px',
}));

const loadMoreConversations = () => {
  if (!isLoadMoreArmed.value) return;

  isLoadMoreArmed.value = false;
  emit('loadMore');
};

const clearConversationAnimations = () => {
  movedConversationDirections.value = new Map();
  newConversationIds.value = new Set();
  clearTimeout(movementAnimationTimer.value);
  movementAnimationTimer.value = null;
};

const onListScroll = () => {
  isLoadMoreArmed.value = true;

  if (!isUserScrolling.value) {
    clearConversationAnimations();
  }

  isUserScrolling.value = true;
  clearTimeout(scrollIdleTimer.value);
  scrollIdleTimer.value = setTimeout(() => {
    isUserScrolling.value = false;
    scrollIdleTimer.value = null;
  }, 180);
};

watch(conversationIds, (currentIds, previousIds = []) => {
  if (!currentIds.length) {
    isLoadMoreArmed.value = false;
    return;
  }

  if (!previousIds.length) return;

  const previousIndexById = previousIds.reduce((result, id, index) => {
    result.set(id, index);
    return result;
  }, new Map());

  if (isUserScrolling.value || conversationListRef.value?.scrollTop > 0) {
    clearConversationAnimations();
    return;
  }

  const newIds = currentIds.reduce((result, id) => {
    if (!previousIndexById.has(id)) result.add(id);
    return result;
  }, new Set());

  const changedPositionDirections = currentIds.reduce((result, id, index) => {
    if (!previousIndexById.has(id)) return result;

    const previousIndex = previousIndexById.get(id);
    if (previousIndex === index) return result;

    result.set(id, previousIndex > index ? 'up' : 'down');
    return result;
  }, new Map());

  const animatedItemCount = newIds.size + changedPositionDirections.size;
  if (!animatedItemCount || animatedItemCount > 8) return;

  movedConversationDirections.value = changedPositionDirections;
  newConversationIds.value = newIds;
  clearTimeout(movementAnimationTimer.value);
  movementAnimationTimer.value = setTimeout(() => {
    clearConversationAnimations();
  }, 700);
});

onBeforeUnmount(() => {
  clearTimeout(movementAnimationTimer.value);
  clearTimeout(scrollIdleTimer.value);
});

defineExpose({ conversationListRef });
</script>

<template>
  <div
    ref="conversationListRef"
    class="flex-1 min-h-0 overflow-y-auto conversations-list [overflow-anchor:none] transition-opacity duration-300 ease-out motion-reduce:transition-none"
    :class="{ 'opacity-60 pointer-events-none': isRefreshing }"
    :aria-busy="isRefreshing"
    @scroll.passive="onListScroll"
  >
    <Virtualizer
      ref="virtualListRef"
      v-slot="{ item }"
      :data="conversationList"
      class="[&>div:has(+_div_.active)>*]:!border-n-surface-1 [&>div:has(+_div_.selected)>*]:!border-n-surface-1"
    >
      <div>
        <ConversationItem
          :key="item.id"
          :source="item"
          :label="label"
          :team-id="teamId"
          :folders-id="foldersId"
          :conversation-type="conversationType"
          :show-assignee="showAssignee"
          :show-expanded="showExpandedCards"
          :position-change-direction="movedConversationDirections.get(item.id)"
          :is-new-conversation="newConversationIds.has(item.id)"
        />
        <div
          v-if="item.id === lastPinnedId"
          role="separator"
          :aria-label="$t('CHAT_LIST.PINNED_SEPARATOR')"
          class="mx-5 my-2 border-t border-n-weak opacity-60"
          data-testid="pinned-conversations-separator"
        />
      </div>
    </Virtualizer>
    <template v-if="isLoading">
      <div v-if="!hideLoadingIndicator" class="flex justify-center my-4">
        <Spinner class="text-n-brand" />
      </div>
    </template>
    <div
      v-else-if="showEndOfListMessage"
      class="flex items-center gap-3 px-4 py-5"
    >
      <span class="h-px flex-1 bg-n-weak" />
      <span
        class="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-n-slate-10"
      >
        <Icon icon="i-hugeicons-inbox-check" class="size-4 text-n-slate-11" />
        {{ $t('CHAT_LIST.EOF') }}
      </span>
      <span class="h-px flex-1 bg-n-weak" />
    </div>
    <IntersectionObserver
      v-else
      :options="intersectionObserverOptions"
      @observed="loadMoreConversations"
    />
  </div>
</template>
